import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Card, CardBody, CardHeader, Button, Row, Col, Badge, Spinner, Modal, ModalHeader, ModalBody 
} from 'reactstrap';
import { deliveriesClient } from '../../auth/keycloakProvider';
import { TransportNoteDetailDto } from '../../web-api-client';
import { ErrorAlert } from '../shared/ErrorAlert';
import { getErrorMessage } from '../../lib/error-utils';

export const TransportNoteDetails: React.FC = () => {
  const navigate = useNavigate();
  const { noteId } = useParams<{ noteId: string }>();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<TransportNoteDetailDto | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  useEffect(() => {
    if (noteId) {
      loadNoteDetails(parseInt(noteId));
    }
  }, [noteId]);

  const loadNoteDetails = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const result = await deliveriesClient.getTransportNote(id);
      setNote(result);
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to load transport note details');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

    const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return '🚨';
      case 'high': return '⚠️';
      case 'medium': return 'ℹ️';
      case 'low': return '✓';
      default: return '';
    }
  };

  const toggleImageModal = () => setImageModalOpen(!imageModalOpen);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading transport note details...</p>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="transport-note-details">
        <Button 
          color="link" 
          className="mb-3 p-0"
          onClick={() => navigate(-1)}
        >
          ← Back
        </Button>
        <ErrorAlert error={error || 'Transport note not found'} onDismiss={() => setError(null)} />
      </div>
    );
  }

  return (
    <div className="transport-note-details">
      <Button 
        color="link" 
        className="mb-3 p-0"
        onClick={() => navigate(-1)}
      >
        ← Back
      </Button>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Transport Note Details</h3>
          {note.isResolved ? (
            <Badge color="success" pill>✓ Resolved</Badge>
          ) : (
            <Badge color="warning" pill>⏳ Pending</Badge>
          )}
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6}>
              <div className="mb-4">
                <h5 className="text-muted mb-3">Basic Information</h5>
                
                <div className="mb-3">
                  <strong>Note ID:</strong>
                  <p className="mb-1">{note.id}</p>
                </div>

                <div className="mb-3">
                  <strong>Note Type:</strong>
                  <p className="mb-1">
                    <Badge color="primary">{note.noteType}</Badge>
                  </p>
                </div>

                <div className="mb-3">
                  <strong>Severity:</strong>
                  <p className="mb-1">
                    <Badge color={getSeverityColor(note.severity.toString())}>
                        {getSeverityIcon(note.severity.toString())} {note.severity}
                    </Badge>
                  </p>
                </div>

                <div className="mb-3">
                  <strong>Reported By:</strong>
                  <p className="mb-1">{note.reportedBy}</p>
                </div>

                <div className="mb-3">
                  <strong>Reported At:</strong>
                  <p className="mb-1">{new Date(note.reportedAt).toLocaleString()}</p>
                </div>

                {note.location && (
                  <div className="mb-3">
                    <strong>Location:</strong>
                    <p className="mb-1">📍 {note.location}</p>
                  </div>
                )}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-4">
                <h5 className="text-muted mb-3">Related Document</h5>
                
                <div className="mb-3">
                  <strong>Document Number:</strong>
                  <p className="mb-1">
                    <Button
                      color="link"
                      className="p-0"
                      onClick={() => navigate(`/documents/${note.documentUUID}`)}
                    >
                      {note.documentNumber}
                    </Button>
                  </p>
                </div>

                <div className="mb-3">
                  <strong>Document UUID:</strong>
                  <p className="mb-1 text-muted" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    {note.documentUUID}
                  </p>
                </div>
              </div>

              {note.isResolved && (
                <div className="mb-4">
                  <h5 className="text-muted mb-3">Resolution</h5>
                  
                  {note.resolvedAt && (
                    <div className="mb-3">
                      <strong>Resolved At:</strong>
                      <p className="mb-1">{new Date(note.resolvedAt).toLocaleString()}</p>
                    </div>
                  )}

                  {note.resolution && (
                    <div className="mb-3">
                      <strong>Resolution Details:</strong>
                      <p className="mb-1">{note.resolution}</p>
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>

          <div className="mb-4">
            <h5 className="text-muted mb-3">Description</h5>
            <div className="p-3 bg-light rounded">
              {note.description}
            </div>
          </div>

          {note.imagePath && (
            <div className="mb-4">
              <h5 className="text-muted mb-3">Attachment</h5>
              <div className="text-center">
                <img 
                  src={note.imagePath} 
                  alt="Transport note attachment" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '400px',
                    cursor: 'pointer',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '5px'
                  }}
                  onClick={toggleImageModal}
                />
                <p className="text-muted mt-2">
                  <small>Click image to view full size</small>
                </p>
              </div>
            </div>
          )}

          <div className="d-flex gap-2 mt-4">
            <Button 
              color="secondary" 
              onClick={() => navigate(-1)}
            >
              ← Back
            </Button>
            <Button 
              color="primary" 
              onClick={() => navigate(`/documents/${note.documentUUID}`)}
            >
              View Document
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Image Modal for full-size view */}
      <Modal isOpen={imageModalOpen} toggle={toggleImageModal} size="lg">
        <ModalHeader toggle={toggleImageModal}>
          Attachment - Full Size
        </ModalHeader>
        <ModalBody className="text-center">
          {note?.imagePath && (
            <img 
              src={note.imagePath} 
              alt="Transport note attachment" 
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};
